'use client';

import type { InitializeAppResponse } from 'src/api/responses';
import { useIsMutating, useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { APP_STATE_KEY } from 'src/constants/session';
import { QUERY_KEYS } from 'src/constants/queries';
import { App } from 'src/api/App';
import useApi from './useApi';
import useAppStorage from './useAppStorage';
import useFeatures from './useFeatures';
import useTeams from './useTeams';
import useUserDetails from './useUserDetails';
export interface AppState {
  sessionToken: string;
}
const isAppStateResponse = (data: unknown): data is InitializeAppResponse => {
  if (typeof data !== 'object' || data === null) return false;
  if (!('session_token' in data) || typeof data.session_token !== 'string') return false;
  return true;
};
interface MutationParams {
  userId?: string;
  teamId?: string;
}
const useAppState = () => {
  const {
    setItem
  } = useAppStorage();
  const {
    userDetails,
    isLoading: isLoadingUser
  } = useUserDetails();
  const {
    currentTeam,
    isLoading: isLoadingTeam
  } = useTeams();
  const appStateClient = useApi(App);
  const [dependenciesLoaded, setDependenciesLoaded] = useState<boolean>(false);
  const initializedFor = useRef<string | null>(null);
  const [currentState, setCurrentState] = useState<AppState | null>(null);
  const {
    setFeatures
  } = useFeatures();
  const {
    id: userId
  } = userDetails || {};
  const {
    id: teamId
  } = currentTeam || {};
  const {
    mutateAsync: initApp
  } = useMutation({
    // queryKey: [QUERY_KEYS.INITIALIZE, userId || 'null', teamId || 'null'],
    mutationKey: [QUERY_KEYS.INITIALIZE, teamId || 'unknown'],
    mutationFn: async ({
      userId: userId_0,
      teamId: teamId_0
    }: MutationParams) => {
      const response = await appStateClient.initialize({
        api_version: 'v1',
        app_environment: 'web',
        app_id: 'dev.effektify.one',
        user_id: userId_0,
        team_id: teamId_0
      });
      const {
        data
      } = response;
      if (!isAppStateResponse(data)) {
        throw new Error('Invalid app state response format');
      }
      updateAppState(data);
    }
  });

  // Read from the mutation cache, not the observer's `isPending`: under Strict Mode the effect
  // below mutates on mount, and the simulated unmount detaches the observer from that mutation
  // for good, leaving `isPending` stuck at true.
  const isLoading = useIsMutating({
    mutationKey: [QUERY_KEYS.INITIALIZE]
  }) > 0;
  const updateAppState = useCallback((appState: InitializeAppResponse) => {
    const {
      features,
      session_token
    } = appState;
    const newState = {
      sessionToken: session_token
    };
    setFeatures(features || []);
    setCurrentState(newState);
    setItem(APP_STATE_KEY, JSON.stringify(newState));
  }, [setItem, setFeatures]);
  const initialize = useCallback(async () => {
    try {
      if (!userId || !teamId) {
        return;
      }
      await initApp({
        userId,
        teamId
      });
    } catch (error) {
      console.error('Error initializing app state:', error);
      throw error; // Re-throw the error to handle it in the component
    }
  }, [userId, teamId, initApp]);
  useEffect(() => {
    if (!isLoadingTeam && !isLoadingUser && !dependenciesLoaded) {
      // Gates the whole authenticated shell — do not derive. Routes without a team (e.g.
      // /switch-company) must still render, so this waits on loading only, not on the ids.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDependenciesLoaded(true);
    }
  }, [dependenciesLoaded, isLoadingTeam, isLoadingUser]);
  useEffect(() => {
    if (!userId || !teamId) return;
    const key = `${userId}:${teamId}`;
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    initialize();
  }, [userId, teamId, initialize]);
  return {
    initialize,
    isLoading: isLoading || !dependenciesLoaded,
    currentState
  };
};
export default useAppState;